import { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import type {
  MultiGetUserFeedbackRequest,
  MultiGetUserFeedbackResponse,
} from '@coursera/grpc-types-userfeedback/coursera/proto/userfeedback/v1beta1/user_feedback_api';

import type { CollectionResult } from 'bundles/program-skills-objectives/components/ProgramSkillRecommendationsPage/SkillCollectionDataProvider';

export const feedbackQuery = gql`
  query feedbackQuery($request: Object!) {
    FeedbackQuery(input: $request)
      @rest(path: "grpc/userfeedback/v1beta1/UserFeedbackAPI/MultiGetUserFeedback", type: "Feedback", method: "POST") {
      userFeedbacks
    }
  }
`;

export type Props = { userId: number; collection: CollectionResult | null | undefined; feedbackContext: string };
export type FeebackOption = 'Yes' | 'No' | 'Hidden' | '';

export const usePreviousFeedback = ({ userId, collection, feedbackContext }: Props) => {
  const [negativeFeedback, setNegativeFeedback] = useState<string[]>([]);
  const [positiveFeedback, setPositiveFeedback] = useState<string[]>([]);
  const [hiddenNegativeFeedback, setHiddenNegativeFeedback] = useState<string[]>([]);

  const { loading, data } = useQuery<
    { FeedbackQuery?: MultiGetUserFeedbackResponse },
    { request: MultiGetUserFeedbackRequest }
  >(feedbackQuery, {
    skip: !userId || !collection,
    fetchPolicy: 'cache-and-network',
    variables: {
      request: {
        userId,
        feedbackTargetIds: [...(collection?.courseIds ?? []), ...(collection?.s12nIds ?? [])].map(
          (id) => `${feedbackContext}/${id}`
        ),
      },
    },
  });

  useEffect(() => {
    if (!loading) {
      const oldNegativeFeedbackRecs =
        data?.FeedbackQuery?.userFeedbacks
          .filter((f) => f.feedback === 'No')
          .map((f) => f.feedbackTargetId.replace(`${feedbackContext}/`, '')) ?? [];

      const oldPositiveFeedbackRecs =
        data?.FeedbackQuery?.userFeedbacks
          .filter((f) => f.feedback === 'Yes')
          .map((f) => f.feedbackTargetId.replace(`${feedbackContext}/`, '')) ?? [];

      setHiddenNegativeFeedback(oldNegativeFeedbackRecs);
      setPositiveFeedback(oldPositiveFeedbackRecs);
    }
  }, [loading, data?.FeedbackQuery?.userFeedbacks, feedbackContext]);

  const recommendationFeedback: { [id: string]: FeebackOption } = {};
  positiveFeedback.forEach((id) => {
    recommendationFeedback[id] = 'Yes';
  });
  negativeFeedback.forEach((id) => {
    recommendationFeedback[id] = 'No';
  });
  hiddenNegativeFeedback.forEach((id) => {
    recommendationFeedback[id] = 'Hidden';
  });

  const giveFeedback = (id: string, feedback: FeebackOption) => {
    if (feedback === 'Yes') {
      setPositiveFeedback((prevFeedback) => [...prevFeedback, id]);
    }
    if (feedback === 'No') {
      setNegativeFeedback((prevFeedback) => [...prevFeedback, id]);
    }
    if (!feedback) {
      setPositiveFeedback((feedbackItems) => [...feedbackItems.filter((feedbackId) => feedbackId !== id)]);
      setNegativeFeedback((feedbackItems) => [...feedbackItems.filter((feedbackId) => feedbackId !== id)]);
      setHiddenNegativeFeedback((feedbackItems) => [...feedbackItems.filter((feedbackId) => feedbackId !== id)]);
    }
  };

  return { giveFeedback, isFeedbackLoading: loading, recommendationFeedback };
};

export default usePreviousFeedback;
